import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Brain, Pencil, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/input';
import { Spinner } from '@/components/ui/states';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

/** Bộ nhớ khách lâu dài (CR-014 bước 4, DEC-230): AI tóm tắt theo khách qua mọi kênh; NV cập nhật ngay hoặc sửa tay. */
export interface MemoryFacts {
  needs: string[];
  products: string[];
  prices: string[];
  preferences: string[];
  nextSteps: string[];
}
export interface CustomerMemory {
  customerId: number;
  summary: string;
  facts: MemoryFacts;
  model: string | null;
  updatedAt: string;
  updatedByName: string | null;
}
interface ConversationMemory {
  customerId: number | null;
  memory: CustomerMemory | null;
  conversationSummary: string | null;
  summarizedAt: string | null;
  pending: number;
}

const FACT_LABELS: [keyof MemoryFacts, string][] = [
  ['needs', 'Nhu cầu'],
  ['products', 'Mặt hàng quan tâm'],
  ['prices', 'Giá đã báo'],
  ['preferences', 'Thói quen, điều kiện'],
  ['nextSteps', 'Việc cần làm tiếp'],
];
const errMsg = (e: unknown) => (e instanceof Error ? e.message : 'Có lỗi, thử lại sau');
const fmt = (iso: string) => new Date(iso).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

function Facts({ facts }: { facts: MemoryFacts }) {
  const rows = FACT_LABELS.filter(([k]) => facts[k]?.length);
  if (!rows.length) return null;
  return (
    <dl className="mt-2 space-y-1.5 text-xs">
      {rows.map(([k, label]) => (
        <div key={k}>
          <dt className="font-medium text-muted-foreground">{label}</dt>
          <dd>
            <ul className="list-disc pl-4">
              {facts[k].map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          </dd>
        </div>
      ))}
    </dl>
  );
}

function MemoryBody({
  memory,
  fallback,
  pending,
  busy,
  onRefresh,
  onSave,
  compact,
}: {
  memory: CustomerMemory | null;
  fallback?: string | null;
  pending?: number;
  busy: boolean;
  onRefresh: () => void;
  onSave?: (summary: string) => Promise<unknown>;
  compact?: boolean;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const text = memory?.summary ?? fallback ?? null;
  return (
    <div className="text-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3
          className={cn(
            'flex items-center gap-1.5',
            compact ? 'text-[11px] font-medium tracking-wider text-muted-foreground uppercase' : 'text-sm font-semibold',
          )}
        >
          <Brain className="size-3.5" /> Bộ nhớ khách
        </h3>
        <div className="flex gap-1">
          {onSave && memory && editing === null && (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditing(memory.summary)} title="Sửa tay">
              <Pencil /> Sửa
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={busy}
            onClick={onRefresh}
            title="AI đọc tin nhắn, báo giá, ghi chú mới và cập nhật"
          >
            {busy ? <Spinner className="size-3" /> : <RefreshCw />} Cập nhật
          </Button>
        </div>
      </div>
      {editing !== null && onSave ? (
        <div className="space-y-2">
          <Textarea rows={6} value={editing} onChange={(e) => setEditing(e.target.value)} maxLength={4000} aria-label="Nội dung bộ nhớ khách" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
              Hủy
            </Button>
            <Button size="sm" disabled={!editing.trim()} onClick={() => void onSave(editing.trim()).then(() => setEditing(null))}>
              Lưu
            </Button>
          </div>
        </div>
      ) : text ? (
        <>
          <p className="text-xs whitespace-pre-line">{text}</p>
          {memory && <Facts facts={memory.facts} />}
          <p className="mt-2 text-[11px] text-muted-foreground">
            {memory
              ? `Cập nhật ${fmt(memory.updatedAt)}${memory.updatedByName ? ` · ${memory.updatedByName}` : ' · tự động'}`
              : 'Tóm tắt hội thoại (khách chưa gắn ERP)'}
            {pending ? ` · ${pending} tin mới chưa tóm tắt` : ''}
          </p>
        </>
      ) : (
        <p className="text-xs text-muted-foreground">
          Chưa có. Máy tự tóm tắt khi hội thoại im 30 phút{pending ? ` (${pending} tin chờ)` : ''}, hoặc bấm Cập nhật.
        </p>
      )}
    </div>
  );
}

/** Trong hộp thư: bộ nhớ của khách gắn với hội thoại (chưa gắn thì tóm tắt hội thoại). */
export function ConversationMemoryCard({ conversationId, compact }: { conversationId: number; compact?: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['zalo', 'memory', conversationId],
    queryFn: () => api.get<ConversationMemory>(`/zalo/conversations/${conversationId}/memory`),
  });
  const refresh = useMutation({
    mutationFn: () => api.post<ConversationMemory>(`/zalo/conversations/${conversationId}/memory`),
    onSuccess: (d) => {
      qc.setQueryData(['zalo', 'memory', conversationId], d);
      if (d.customerId) void qc.invalidateQueries({ queryKey: ['customer-memory', d.customerId] });
      toast.success('Đã cập nhật bộ nhớ khách');
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  if (q.isLoading) return <Spinner />;
  if (!q.data) return null;
  return (
    <MemoryBody
      memory={q.data.memory}
      fallback={q.data.conversationSummary}
      pending={q.data.pending}
      busy={refresh.isPending}
      onRefresh={() => refresh.mutate()}
      compact={compact}
    />
  );
}

/** Trang khách hàng: bộ nhớ khách, NV có quyền sửa khách thì sửa tay được. */
export function CustomerMemoryPanel({ customerId }: { customerId: number }) {
  const { can } = useAuth();
  const qc = useQueryClient();
  const key = ['customer-memory', customerId];
  const q = useQuery({ queryKey: key, queryFn: () => api.get<{ memory: CustomerMemory | null }>(`/customers/${customerId}/memory`) });
  const done = (d: { memory: CustomerMemory | null }) => {
    qc.setQueryData(key, d);
    void qc.invalidateQueries({ queryKey: ['zalo', 'memory'] });
  };
  const refresh = useMutation({
    mutationFn: () => api.post<{ memory: CustomerMemory | null }>(`/customers/${customerId}/memory`),
    onSuccess: (d) => {
      done(d);
      toast.success('Đã cập nhật bộ nhớ khách');
    },
    onError: (e) => toast.error(errMsg(e)),
  });
  const save = (summary: string) =>
    api.put<{ memory: CustomerMemory | null }>(`/customers/${customerId}/memory`, { summary }).then(
      (d) => {
        done(d);
        toast.success('Đã lưu');
      },
      (e) => toast.error(errMsg(e)),
    );
  return (
    <div className="rounded-md border bg-card p-4">
      {q.isLoading ? (
        <Spinner />
      ) : (
        <MemoryBody
          memory={q.data?.memory ?? null}
          busy={refresh.isPending}
          onRefresh={() => refresh.mutate()}
          onSave={can('customer.update') ? save : undefined}
        />
      )}
    </div>
  );
}
